import { Fragment, useEffect, useState, useMemo } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import MpgInput from "../../../../CommonFormComp/MpgInput";
import MpgItem from "../../../../CommonFormComp/MpgItem";
import MpgAutocompleteSingle from "../../../../CommonFormComp/MpgAutocompleteSingle";
import {
  adminUserViewPath,
} from "../../../../routes/routePaths";
import MpgErrorDialog from "../../../../common/dialog/MpgErrorDialog";
import MpgLoadingDialog from "../../../../common/dialog/MpgLoadingDialog";
import MpgAutocompleteMulti from "../../../../CommonFormComp/MpgAutocompleteMulti";
import { useGetLoggedInUserDetails } from "../../../../hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { createAdminUserValidation } from "./createAdminUserValidation";
import {
  useCreateAdminUserMutation,
  useGetCountryListOnlyForRightTractByLocaleQuery,
  useGetCountrySpecificLanguageListByCountryCodeQuery,
  useGetCronofyServiceAccountDataQuery,
  useGetCronofyTimeZoneDataByCountryCodeQuery,
  useGetServiceOfficeListByCountryCodesQuery,
  useGetTimeZoneListForAdminUserQuery,
  useVerifyActiveAdminUserEmailIdFromADQuery,
} from "../../../../reactQueries/AppApi/rqHooksForAdminUser";
import { useGetWorkingHoursList } from "../../../../hooksAndFunctions/customHooks/useGetWorkingHoursList";
import { useGetDayListOfWeek } from "../../../../hooksAndFunctions/customHooks/useGetDayListOfWeek";
import { useGetAdminRoleGrouping } from "../../../../hooksAndFunctions/customHooks/useGetAdminRoleGrouping";
import MpgCheckbox from "../../../../CommonFormComp/MpgCheckbox";
import SearchForActiveUserEmail from "./SearchForActiveUserEmail";
import FormHelperText from "@mui/material/FormHelperText";
import { useAccessTypes } from "../../../../hooksAndFunctions/customHooks/useAccessTypes";
import useTrackUserActivityUsingPhaseOneAPI from "../../../../userActivityTracking/useTrackUserActivityUsingPhaseOneAPI";
import useTrackUserActivity from "../../../../userActivityTracking/useTrackUserActivity";
import sortBy from "lodash/sortBy";
import { useGetSiteCoreCMSData } from "../../../../reactQueries/commonRQHooks";
import AdminUserProfileSection from "../AdminUserProfileSection";

const CreateAdminUser = () => {
  let navigate = useNavigate();

  const { loggedInUser } = useGetLoggedInUserDetails();
  const { activeDirectoryId, lastName, firstName } = loggedInUser;
  const [disableFirstName, setDisableFirstName] = useState(true);
  const [disableLastName, setDisableLastName] = useState(true);
  const {
    isEnterpriseAdmin: isEnterpriseAdminType,
    clientPortalAdminType,
    candidatePortalAdminType,
  } = useAccessTypes();
  const { hasA1Access } =
    clientPortalAdminType;
  const {
    hasSuperAdminAccess,
  } = candidatePortalAdminType;

  const userName = `${lastName} ${firstName}`;

  const {
    data: adminUserCMSDataFromAPI,
  } = useGetSiteCoreCMSData({ pagename: "Users" });
  const [adminUserCMSData, setAdminUserCMSData] = useState({
    title: "",
    description: "",
    tabNavigationTitle: "",
    pageContent: {},
  });

  const fetchAdminUserCMSData = () => {
    if (adminUserCMSDataFromAPI && adminUserCMSDataFromAPI.Pages) {
      adminUserCMSDataFromAPI.Pages.map((el) => {
        if (el.Key === "UserAccounts") {
          const { Title, Description, TabNavigationTitle, Components } = el;
          setAdminUserCMSData({
            title: Title,
            description: Description,
            tabNavigationTitle: TabNavigationTitle,
            pageContent: Components,
          });
        }
      });
    }
  };

  const fetchAdminUserValidationLabel = (key) => {
    if (
      adminUserCMSData &&
      adminUserCMSData?.pageContent["AdminUserValidationMsg"]
    ) {
      const adminUserCMSDataLabel =
        adminUserCMSData.pageContent["AdminUserValidationMsg"];
      return returnLabel(adminUserCMSDataLabel?.Labels, key);
    }
    return "";
  };

  const fetchAdminUserDetailsLabel = (key) => {
    if (adminUserCMSData && adminUserCMSData?.pageContent["AdminUserDetails"]) {
      const adminUserCMSDataLabel =
        adminUserCMSData.pageContent["AdminUserDetails"];
      return returnLabel(adminUserCMSDataLabel?.Labels, key);
    }
    return "";
  };

  const fetchCommonCMSLabel = (key) => {
    if (adminUserCMSData && adminUserCMSData?.pageContent["CommonComponent"]) {
      const adminUserCMSDataLabel =
        adminUserCMSData.pageContent["CommonComponent"];
      return returnLabel(adminUserCMSDataLabel?.Labels, key);
    }
    return "";
  };

  const fetchAdminUserRolesLabel = (key) => {
    if (adminUserCMSData && adminUserCMSData?.pageContent["AdminUserRoles"]) {
      const adminUserCMSDataLabel =
        adminUserCMSData.pageContent["AdminUserRoles"];
      return returnLabel(adminUserCMSDataLabel?.Labels, key);
    }
    return "";
  };

  const fetchAdminUserProfileLabel = (key) => {
    if (adminUserCMSData && adminUserCMSData?.pageContent["AdminUserProfile"]) {
      const adminUserCMSDataLabel =
        adminUserCMSData.pageContent["AdminUserProfile"];
      return returnLabel(adminUserCMSDataLabel?.Labels, key);
    }
    return "";
  };

  const fetchCronofySectionLabel = (key) => {
    if (adminUserCMSData && adminUserCMSData?.pageContent["CronofySection"]) {
      const adminUserCMSDataLabel =
        adminUserCMSData.pageContent["CronofySection"];
      return returnLabel(adminUserCMSDataLabel?.Labels, key);
    }
    return "";
  };

  const returnLabel = (labelArray = [], key) => {
    return labelArray.find((x) => x.Key === key)?.Phrase;
  };

  useEffect(() => {
    fetchAdminUserCMSData();
  }, [adminUserCMSDataFromAPI]);

  let { schema } = useMemo(
    () =>
      createAdminUserValidation(
        adminUserCMSData?.pageContent["AdminUserValidationMsg"]?.Labels
      ),
    [adminUserCMSData]
  );
  const rhfFormInstance = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      enterprise_admin_check: false,
    },
  });
  const { watch, setValue } = rhfFormInstance;

  const selectedCountryByLocaleFromUserDetails = watch("country_user_details");
  const selectedCountryByLocaleFromUserRoles = watch("country_user_role");
  const selectedEnterpriseAdminCheck = watch("enterprise_admin_check");

  const selectedCandidatePortalRole = watch("candidate_portal_role");
  const selectedClientPortalRole = watch("client_portal_role");
  const email = watch("email");
  const adminUserFullName = `${watch("last_name")} ${watch("first_name")}`;

  /**get timezonesList */
  const {
    data: timezonesListForAdminUser,
  } = useGetTimeZoneListForAdminUserQuery();

  /**get CountryListByLocale*/
  const {
    data: countryListForRTByLocale,
  } = useGetCountryListOnlyForRightTractByLocaleQuery();

  /**get countrySpLangListByCountryCode */
  const {
    data: countrySpLangListByCountryCode,
  } = useGetCountrySpecificLanguageListByCountryCodeQuery({
    countryCode: selectedCountryByLocaleFromUserDetails?.id,
  });

  /**get ServiceOfficeListByCountryCodes for user details*/
  const {
    data: serviceOfficeListForUserDetails,
  } = useGetServiceOfficeListByCountryCodesQuery({
    countryCodes: selectedCountryByLocaleFromUserDetails?.id,
  });

  /**get ServiceOfficeListByCountryCodes for user roles*/
  let countryCodeArray = [];
  if (!!selectedCountryByLocaleFromUserRoles) {
    selectedCountryByLocaleFromUserRoles.forEach((eachItem) => {
      countryCodeArray.push(eachItem?.id);
    });
  }
  const {
    data: serviceOfficeListForUserRoles,
  } = useGetServiceOfficeListByCountryCodesQuery({
    countryCodes: countryCodeArray.toString(),
  });

  const { hoursList } = useGetWorkingHoursList();

  const { weekDays } = useGetDayListOfWeek();

  const {
    data: cronofyTimeZone,
  } = useGetCronofyTimeZoneDataByCountryCodeQuery({
    countryCode: selectedCountryByLocaleFromUserDetails?.id,
  });

  const {
    data: serviceAccountData,
  } = useGetCronofyServiceAccountDataQuery();

  // api - create client user mutation
  const {
    mutate: createAdminUser,
    isError: isErrorOnCreate,
    isLoading: isLoadingOnCreate,
    isSuccess: isSuccessOnCreate,
    error: errorOnCreate,
  } = useCreateAdminUserMutation();

  /** !!!!!!!! autocomplete option lists !!!!!!! */
  // const timezonesList = !!timezonesListForAdminUser ? timezonesListForAdminUser[0]?.items : []
  const timezonesAutocompleteList =
    !!timezonesListForAdminUser &&
    timezonesListForAdminUser.map((item) => {
      return {
        id: item.id,
        selectOptionLabel: item.title,
        selectOptionValue: item.id,
        extraData: item,
      };
    });

  const weekDaysAutocompleteList =
    !!weekDays &&
    weekDays.map((item) => {
      return {
        id: item.label,
        selectOptionLabel: item.label,
        selectOptionValue: item.value,
        extraData: item,
      };
    });
  const hoursListAutocompleteList =
    !!hoursList &&
    hoursList.map((item) => {
      return {
        id: item.label,
        selectOptionLabel: item.label,
        selectOptionValue: item.label,
        extraData: item,
      };
    });

  const countryListByLocaleAutocompleteList =
    !!countryListForRTByLocale &&
    countryListForRTByLocale.map((item) => {
      const standardCodesList = item?.standardCodes;
      const standardCodesListonlyForRT =
        !!standardCodesList &&
        standardCodesList.find((eachItem) => {
          if (eachItem?.itemCodeStandardName === "rt") {
            return eachItem;
          }
        });
      return {
        id: standardCodesListonlyForRT?.standardCodeValue || "",
        selectOptionLabel: item.title,
        selectOptionValue: standardCodesListonlyForRT?.standardCodeValue || "",
        extraData: item,
      };
    });

  const countrySpLangAutocompleteList =
    !!countrySpLangListByCountryCode &&
    countrySpLangListByCountryCode.map((item) => {
      return {
        id: item.localeName,
        selectOptionLabel: item.localeName,
        selectOptionValue: item.localeName,
        extraData: item,
      };
    });

  const serviceOfficeListForUserDetailsAutocompleteList =
    !!serviceOfficeListForUserDetails &&
    serviceOfficeListForUserDetails.map((item) => {
      return {
        id: item.rightOfficeId,
        selectOptionLabel: item.rightOfficeName,
        selectOptionValue: item.rightOfficeId,
        extraData: item,
      };
    });
  const serviceOfficeListForUserRolesAutocompleteList =
    !!serviceOfficeListForUserRoles &&
    serviceOfficeListForUserRoles.map((item) => {
      return {
        id: item.rightOfficeId,
        selectOptionLabel: item.rightOfficeName,
        selectOptionValue: item.rightOfficeId,
        extraData: item,
      };
    });

  const adminUserStatus = [
    {
      statusTitle: "Active",
      isActive: true,
    },
    {
      statusTitle: "InActive",
      isActive: false,
    },
  ];
  const adminUserStatusAutocompleteList =
    !!adminUserStatus &&
    adminUserStatus.map((item) => {
      return {
        id: item.statusTitle,
        selectOptionLabel: item.statusTitle,
        selectOptionValue: item.isActive,
        extraData: item,
      };
    });

  const cronofyTimeZoneAutocompleteList =
    !!cronofyTimeZone &&
    cronofyTimeZone.map((item) => {
      return {
        id: item.id,
        selectOptionLabel: item.ianaTimeZoneIdentifier,
        selectOptionValue: item.ianaTimeZoneIdentifier,
        extraData: item,
      };
    });

  const serviceAccountDataAutocompleteList =
    !!serviceAccountData &&
    serviceAccountData.map((item) => {
      return {
        id: item.id,
        selectOptionLabel: item.serviceAccountName,
        selectOptionValue: item.id,
        extraData: item,
      };
    });

  const {
    enterpriseAdminRolesGrp,
    candidatePortalRolesGrp,
    clientPortalRolesGrp,
  } = useGetAdminRoleGrouping();

  const candidatePortalRolesAutocompleteList =
    !!candidatePortalRolesGrp &&
    candidatePortalRolesGrp.map((item) => {
      return {
        id: item.roleId,
        selectOptionLabel: item.role,
        selectOptionValue: item.roleId,
        extraData: item,
      };
    });
  const clientPortalRolesAutocompleteList =
    !!clientPortalRolesGrp &&
    clientPortalRolesGrp.map((item) => {
      return {
        id: item.roleId,
        selectOptionLabel: item.role,
        selectOptionValue: item.roleId,
        extraData: item,
      };
    });
  const sortedClientPortalRolesAutocompleteList = sortBy(
    clientPortalRolesAutocompleteList,
    function (item) {
      const role = item?.selectOptionLabel || "";
      /**we are converting all selectOptionLabel into smallcase for proper aphabetical order */
      return [role.toLowerCase()];
    }
  );

  /**set default value for status */
  useEffect(() => {
    setValue("status", adminUserStatusAutocompleteList[0]);
  }, []);

  /** handling disabled fields */

  const [
    disabledIsEnterpriseAdminRoleBasedOnAccess,
    setDisabledIsEnterpriseAdminRoleBasedOnAccess,
  ] = useState(false);
  const [
    disabledCandidatePortalRoleBasedOnAccess,
    setDisabledCandidatePortalRoleBasedOnAccess,
  ] = useState(false);
  const [
    disabledClientPortalRoleBasedOnAccess,
    setDisabledClientPortalRoleBasedOnAccess,
  ] = useState(false);

  // /**for rolebased access disable field */
  useEffect(() => {
    if (!!isEnterpriseAdminType) {
      /**disabled state */
      setDisabledIsEnterpriseAdminRoleBasedOnAccess(false);
      setDisabledCandidatePortalRoleBasedOnAccess(false);
      setDisabledClientPortalRoleBasedOnAccess(false);
    } else {
      setDisabledIsEnterpriseAdminRoleBasedOnAccess(true);

      /**access control for candidate portal */
      if (!!hasSuperAdminAccess) {
        setDisabledCandidatePortalRoleBasedOnAccess(false);
      } else {
        setDisabledCandidatePortalRoleBasedOnAccess(true);
      }
      /**access control for client portal */

      if (!!hasA1Access) {
        setDisabledClientPortalRoleBasedOnAccess(false);
      } else {
        setDisabledClientPortalRoleBasedOnAccess(true);
      }
    }
  }, [isEnterpriseAdminType, clientPortalAdminType, candidatePortalAdminType]);

  /**for form interaction disabled field */
  const [
    disabledCandidatePortalRoleOnEntRoleCheck,
    setDisabledCandidatePortalRoleOnEntRoleCheck,
  ] = useState(false);
  const [
    disabledClientPortalRoleOnEntRoleCheck,
    setDisabledClientPortalRoleOnEntRoleCheck,
  ] = useState(false);
  useEffect(() => {
    if (!!selectedEnterpriseAdminCheck) {
      setValue("candidate_portal_role", "");
      setValue("client_portal_role", "");

      setDisabledCandidatePortalRoleOnEntRoleCheck(true);
      setDisabledClientPortalRoleOnEntRoleCheck(true);
    } else {
      setDisabledCandidatePortalRoleOnEntRoleCheck(false);
      setDisabledClientPortalRoleOnEntRoleCheck(false);
    }
  }, [selectedEnterpriseAdminCheck]);

  const [disabledApplicationLanguage, setDisabledApplicationLanguage] =
    useState(false);
  const [
    disabledServiceOfficeUserDetails,
    setDisabledServiceOfficeUserDetails,
  ] = useState(false);
  const [disabledTimeZoneCronofy, setDisabledTimeZoneCronofy] = useState(false);

  useEffect(() => {
    if (!!selectedCountryByLocaleFromUserDetails?.id) {
      setDisabledApplicationLanguage(false);
      setDisabledServiceOfficeUserDetails(false);
      setDisabledTimeZoneCronofy(false);
    } else {
      setDisabledApplicationLanguage(true);
      setDisabledServiceOfficeUserDetails(true);
      setDisabledTimeZoneCronofy(true);
    }
  }, [selectedCountryByLocaleFromUserDetails?.id]);

  const [disabledServiceOffFromUserRoles, setDisabledServiceOffFromUserRoles] =
    useState(false);
  useEffect(() => {
    if (
      !!selectedCountryByLocaleFromUserRoles &&
      !!selectedCountryByLocaleFromUserRoles.length
    ) {
      setDisabledServiceOffFromUserRoles(false);
    } else {
      setDisabledServiceOffFromUserRoles(true);
    }

    /**clear value on every country change */
    setValue("service_office_user_role", "");
  }, [selectedCountryByLocaleFromUserRoles]);

  // console.log("selectedCandidatePortalRole", selectedCandidatePortalRole)

  const [showCoachRelatedContent, setShowCoachRelatedContent] = useState(false);
  useEffect(() => {
    if (
      selectedCandidatePortalRole?.selectOptionLabel === "Coach" ||
      selectedCandidatePortalRole?.selectOptionLabel === "Coach Admin"
    ) {
      setShowCoachRelatedContent(true);
    } else {
      setShowCoachRelatedContent(false);

      /**clear all coach related fields*/
      setValue("bio_user_profile", "");
      setValue("expertise", "");
      setValue("language_user_profile", "");
      setValue("linkedIn_id", "");
      setValue("twitter_id", "");

      setValue("time_zone_cronofy", "");
      setValue("service_account_name_cronofy", "");
      setValue("available_days", "");
      setValue("working_hours_start_time", "");
      setValue("working_hours_end_time", "");
    }
  }, [selectedCandidatePortalRole]);

  /**form handling */

  // console.log("enterpriseAdminRolesGrp", enterpriseAdminRolesGrp[0])
  const onSubmit = (data) => {
    // console.log("data:", data)

    /**common body handling */

    let serviceOfficeArrayFromUserRoles = [];
    const serviceOfficesFromRole = data?.service_office_user_role || [];

    if (!!serviceOfficesFromRole && !!serviceOfficesFromRole.length) {
      serviceOfficesFromRole.forEach((eachItem) => {
        serviceOfficeArrayFromUserRoles.push({
          ServiceOfficeId: eachItem?.extraData?.rightOfficeId || "",
          ServiceOfficeName: eachItem?.extraData?.rightOfficeName || "",
          CountryCode: eachItem?.extraData?.countryCode || "",
        });
      });
    }

    const enterpriseAdminRoleData = enterpriseAdminRolesGrp[0];
    let roles = {
      candidatePortalRole: {
        role: !!data?.candidate_portal_role
          ? data?.candidate_portal_role?.extraData?.role
          : "",
        roleId: !!data?.candidate_portal_role
          ? data?.candidate_portal_role?.extraData?.roleId
          : "00000000-0000-0000-0000-000000000000",
      },
      clientPortalRole: {
        role: !!data?.client_portal_role
          ? data?.client_portal_role?.extraData?.role
          : "",
        roleId: data?.client_portal_role
          ? data?.client_portal_role?.extraData?.roleId
          : "00000000-0000-0000-0000-000000000000",
      },
      enterpriseRole: {
        role: !!data?.enterprise_admin_check
          ? enterpriseAdminRoleData?.role
          : "",
        roleId: !!data?.enterprise_admin_check
          ? enterpriseAdminRoleData?.roleId
          : "00000000-0000-0000-0000-000000000000",
      },
    };

    const commonBody = {
      ActiveDirectoryId: activeDirectoryId || "",
      FirstName: data?.first_name || "",
      LastName: data?.last_name || "",
      PreferredName: data?.preferred_name || "",
      AffiliateEmailAddress: data?.affiliate_email || "",
      CountryCode: data?.country_user_details?.selectOptionValue || "",
      EmailAddress: data?.email || "",
      timeZone: data?.time_zone?.id || "",
      ContactNo: data?.preferred_number || "",
      ApplicationLanguage: data?.application_language?.selectOptionLabel || "",
      ServiceOfficeId:
        data?.service_office_user_details?.extraData?.rightOfficeId || "",
      ServiceOfficeName:
        data?.service_office_user_details?.extraData?.rightOfficeName || "",
      IsActive: data?.status?.selectOptionValue,
      IsEnterpriseAdmin: data?.enterprise_admin_check || false,
      AdminUserRoles: {
        ...roles,
        serviceOffices: serviceOfficeArrayFromUserRoles || [],
      },
      Username: `${data?.last_name},${data?.first_name}`,
      CreatedBy: userName,
    };

    /**coach related body handling */
    let weeklyPeriods = [];

    if (!!data?.available_days) {
      data?.available_days.forEach((item) => {
        weeklyPeriods.push({
          day: item?.selectOptionValue || "",
          start_time: data?.working_hours_start_time?.selectOptionValue || "",
          end_time: data?.working_hours_end_time?.selectOptionValue || "",
        });
      });
    }

    const coachRelatedBody = {
      ServiceAccountId: data?.service_account_name_cronofy?.id || "",
      schedulingAvailability: {
        tzid: data?.time_zone_cronofy?.selectOptionValue || "",
        weekly_periods: weeklyPeriods || [],
      },
      Bio: data?.bio_user_profile || "",
      Expertise: data?.expertise || "",
      Language: data?.language_user_profile || "",
      SocialIdDetails: `${data?.linkedIn_id},${data?.twitter_id}`,
    };

    let body = {};

    if (!!showCoachRelatedContent) {
      body = {
        ...commonBody,
        ...coachRelatedBody,
      };
    } else {
      body = commonBody;
    }

    // console.log("body:", body)

    createAdminUser({ body });
  };

  useEffect(() => {
    if (!!isSuccessOnCreate) {
      navigate(adminUserViewPath);
    }
  }, [isSuccessOnCreate]);

  /*** active user search functionality handling */
  const [inputEmail, setInputEmail] = useState("");
  const {
    isSuccess: isSuccessOnEmailSearchAPI,
    isFetching: isFetchingOnEmailSearchAPI,
    error: errorOnEmailSearchAPI,
    isError: isErrorOnEmailSearchAPI,
    data: userDataOnEmailSearchAPI,
  } = useVerifyActiveAdminUserEmailIdFromADQuery({ emailId: inputEmail });

  useEffect(() => {
    if (!!userDataOnEmailSearchAPI && !!isSuccessOnEmailSearchAPI) {
      setValue("first_name", userDataOnEmailSearchAPI?.givenName || "");
      setValue("last_name", userDataOnEmailSearchAPI?.surname || "");
      setValue("email", userDataOnEmailSearchAPI?.mail || "");
      if (
        userDataOnEmailSearchAPI?.mail &&
        (userDataOnEmailSearchAPI.givenName === null ||
          userDataOnEmailSearchAPI.surname === null)
      ) {
        setDisableFirstName(false);
        setDisableLastName(false);
      }
    } else {
      setValue("first_name", "");
      setValue("last_name", "");
      setValue("email", "");
      setDisableFirstName(true);
      setDisableLastName(true);
    }
  }, [isSuccessOnEmailSearchAPI, userDataOnEmailSearchAPI]);

  const [customError, setCustomError] = useState("");
  useEffect(() => {
    if (!!isErrorOnEmailSearchAPI) {
      const graphAPIErrorCode =
        errorOnEmailSearchAPI?.response?.data?.error?.code;

      // console.log("graphAPIErrorCode:", graphAPIErrorCode)
      if (graphAPIErrorCode === "Request_ResourceNotFound") {
        setCustomError(
          fetchAdminUserValidationLabel("AdminUserValidationMsg.EmailNotExist")
        );
        setDisableFirstName(true);
        setDisableLastName(true);
      } else {
        setCustomError(
          fetchAdminUserValidationLabel(
            "AdminUserValidationMsg.SomethingWentWrong"
          )
        );
      }
    } else {
      setCustomError("");
    }
  }, [isErrorOnEmailSearchAPI, errorOnEmailSearchAPI]);

  /**tracking user Activity */
  const { trackActivity } = useTrackUserActivity();
  const { trackActivityUsingPhaseOneAPI } =
    useTrackUserActivityUsingPhaseOneAPI();
  useEffect(() => {
    if (!!isSuccessOnCreate) {
      trackActivity({
        activitySuccess: !!isSuccessOnCreate,
        actionType: "Created",
        itemId: email || "",
      });

      let roles = "";
      if (!!selectedEnterpriseAdminCheck) {
        roles = "Enterprise admin";
      } else {
        if (
          !!selectedCandidatePortalRole?.selectOptionLabel &&
          !!selectedClientPortalRole?.selectOptionLabel
        ) {
          roles = `${selectedCandidatePortalRole?.selectOptionLabel} and ${selectedClientPortalRole?.selectOptionLabel}`;
        } else {
          roles =
            `${selectedCandidatePortalRole?.selectOptionLabel}` ||
            `${selectedClientPortalRole?.selectOptionLabel}` ||
            "";
        }
      }
      trackActivityUsingPhaseOneAPI({
        activitySuccess: !!isSuccessOnCreate,
        actionType: "Created",
        itemId: email || "",
        extraDescription: `${adminUserFullName} as ${roles}`,
      });
    }
  }, [isSuccessOnCreate]);

  /**error handling */
  const [errorTitle, setErrorTitle] = useState("");
  const [errorContent, setErrorContent] = useState("");

  useEffect(() => {
    if (
      !!errorOnCreate &&
      errorOnCreate?.response?.data?.errorType === "EmailExists"
    ) {
      setErrorTitle(
        fetchAdminUserValidationLabel("AdminUserValidationMsg.EmailExists")
      );
      setErrorContent(
        `"${watch("email")}" ${fetchAdminUserValidationLabel(
          "AdminUserValidationMsg.EmailExistsMsg"
        ).replace("*Email*", "")}`
      );
    } else if (
      !!errorOnCreate &&
      errorOnCreate?.response?.data?.errorType === "ValidationError"
    ) {
      setErrorTitle(
        fetchAdminUserValidationLabel("AdminUserValidationMsg.FormValidation")
      );
      setErrorContent(
        fetchAdminUserValidationLabel(
          "AdminUserValidationMsg.FormValidationForRequiredFields"
        )
      );
    }
  }, [errorOnCreate]);

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <Typography variant="h5" align="center" sx={{ fontWeight: "bolder" }}>
          {fetchAdminUserDetailsLabel("AdminUserDetails.CreateNewAdminUser")}
        </Typography>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
          m={1}
        >
          <Button
            variant="outlined"
            size="small"
            component={Link}
            to={adminUserViewPath}
          >
            {fetchAdminUserDetailsLabel("AdminUserDetails.AdminUsers")}
          </Button>
        </Stack>

        <Box
          component="form"
          noValidate
          autoComplete="off"
          onSubmit={rhfFormInstance.handleSubmit(onSubmit)}
        >
          <MpgItem sx={{ padding: { xs: "20px" } }}>
            <Typography
              variant="h6"
              align="center"
              sx={{ fontWeight: "bolder" }}
            >
              {fetchAdminUserDetailsLabel(
                "AdminUserDetails.SearchActiveUserDetails"
              )}
            </Typography>

            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="center"
            >
              <Grid
                sx={{
                  width: {
                    sm: "300px",
                    md: "500px",
                    lg: "700px",
                    xl: "900px",
                  },
                }}
              >
                <SearchForActiveUserEmail
                  setInputEmail={setInputEmail}
                  label={fetchAdminUserDetailsLabel(
                    "AdminUserDetails.SearchEmailId"
                  )}
                  cmsValidationMsgLabels={
                    adminUserCMSData?.pageContent["AdminUserValidationMsg"]
                      ?.Labels
                  }
                />
                {!!isFetchingOnEmailSearchAPI &&
                  fetchCommonCMSLabel("Common.Waitloading")}
                <FormHelperText error={!!isErrorOnEmailSearchAPI}>
                  {customError}
                </FormHelperText>
              </Grid>
            </Grid>
          </MpgItem>
          <br />

          <MpgItem sx={{ padding: { xs: "20px" } }}>
            <Typography
              variant="h6"
              align="center"
              sx={{ fontWeight: "bolder" }}
            >
              {adminUserCMSData?.pageContent["AdminUserDetails"]?.Title}
            </Typography>

            <Grid container spacing={1}>
              <Grid item xs={12} sm={12} md={6}>
                <MpgInput
                  rhfFormInstance={rhfFormInstance}
                  name={"first_name"}
                  label={fetchAdminUserDetailsLabel(
                    "AdminUserDetails.FirstName"
                  )}
                  fullWidth
                  disabled={disableFirstName}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <MpgInput
                  rhfFormInstance={rhfFormInstance}
                  name={"last_name"}
                  label={fetchAdminUserDetailsLabel(
                    "AdminUserDetails.LastName"
                  )}
                  fullWidth
                  disabled={disableLastName}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <MpgInput
                  rhfFormInstance={rhfFormInstance}
                  name={"preferred_name"}
                  label={fetchAdminUserDetailsLabel(
                    "AdminUserDetails.Preferred Name"
                  )}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <MpgInput
                  rhfFormInstance={rhfFormInstance}
                  name={"email"}
                  label={fetchAdminUserDetailsLabel("AdminUserDetails.Email")}
                  fullWidth
                  disabled
                  required
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <MpgInput
                  rhfFormInstance={rhfFormInstance}
                  name={"affiliate_email"}
                  label={fetchAdminUserDetailsLabel(
                    "AdminUserDetails.AffiliateEmailAddress"
                  )}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <MpgInput
                  rhfFormInstance={rhfFormInstance}
                  name={"preferred_number"}
                  label={fetchAdminUserDetailsLabel(
                    "AdminUserDetails.PreferredNumber"
                  )}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <MpgAutocompleteSingle
                  rhfFormInstance={rhfFormInstance}
                  name={"time_zone"}
                  label={fetchAdminUserDetailsLabel(
                    "AdminUserDetails.TimeZone"
                  )}
                  fullWidth
                  optionList={timezonesAutocompleteList || []}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <MpgAutocompleteSingle
                  rhfFormInstance={rhfFormInstance}
                  name={"country_user_details"}
                  label={fetchAdminUserDetailsLabel("AdminUserDetails.Country")}
                  fullWidth
                  optionList={countryListByLocaleAutocompleteList || []}
                  onChangeExtraHandler={() => {
                    setValue("application_language", "");
                    setValue("service_office_user_details", "");
                    setValue("time_zone_cronofy", "");
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <MpgAutocompleteSingle
                  rhfFormInstance={rhfFormInstance}
                  name={"application_language"}
                  label={fetchAdminUserDetailsLabel(
                    "AdminUserDetails.ApplicationLanguage"
                  )}
                  optionList={countrySpLangAutocompleteList || []}
                  disabled={!!disabledApplicationLanguage}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <MpgAutocompleteSingle
                  rhfFormInstance={rhfFormInstance}
                  name={"service_office_user_details"}
                  label={fetchAdminUserDetailsLabel(
                    "AdminUserDetails.ServiceOffice"
                  )}
                  optionList={
                    serviceOfficeListForUserDetailsAutocompleteList || []
                  }
                  fullWidth
                  disabled={!!disabledServiceOfficeUserDetails}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <MpgAutocompleteSingle
                  rhfFormInstance={rhfFormInstance}
                  name={"status"}
                  label={fetchAdminUserDetailsLabel("AdminUserDetails.Status")}
                  optionList={adminUserStatusAutocompleteList || []}
                  fullWidth
                  required
                />
              </Grid>
            </Grid>
          </MpgItem>
          <br />

          <MpgItem sx={{ padding: { xs: "20px" } }}>
            <Typography
              variant="h6"
              align="center"
              sx={{ fontWeight: "bolder" }}
            >
              {adminUserCMSData?.pageContent["AdminUserRoles"]?.Title}
            </Typography>

            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
            >
              <MpgCheckbox
                rhfFormInstance={rhfFormInstance}
                name={"enterprise_admin_check"}
                label={fetchAdminUserRolesLabel(
                  "AdminUserRoles.EnterpriseAdmin"
                )}
                disabled={!!disabledIsEnterpriseAdminRoleBasedOnAccess}
              />
            </Grid>

            <Grid container spacing={1}>
              <Grid item xs={12} sm={12} md={6}>
                <MpgAutocompleteSingle
                  rhfFormInstance={rhfFormInstance}
                  name={"candidate_portal_role"}
                  label={fetchAdminUserRolesLabel(
                    "AdminUserRoles.CandidatePortalRole"
                  )}
                  optionList={candidatePortalRolesAutocompleteList || []}
                  fullWidth
                  disabled={
                    !!disabledCandidatePortalRoleOnEntRoleCheck ||
                    !!disabledCandidatePortalRoleBasedOnAccess
                  }
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <MpgAutocompleteSingle
                  rhfFormInstance={rhfFormInstance}
                  name={"client_portal_role"}
                  label={fetchAdminUserRolesLabel(
                    "AdminUserRoles.ClientPortalRole"
                  )}
                  optionList={sortedClientPortalRolesAutocompleteList || []}
                  fullWidth
                  disabled={
                    !!disabledClientPortalRoleOnEntRoleCheck ||
                    !!disabledClientPortalRoleBasedOnAccess
                  }
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <MpgAutocompleteMulti
                  rhfFormInstance={rhfFormInstance}
                  name={"country_user_role"}
                  label={fetchAdminUserRolesLabel("AdminUserRoles.Country")}
                  fullWidth
                  optionList={countryListByLocaleAutocompleteList || []}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <MpgAutocompleteMulti
                  rhfFormInstance={rhfFormInstance}
                  name={"service_office_user_role"}
                  label={fetchAdminUserRolesLabel(
                    "AdminUserRoles.ServiceOffice"
                  )}
                  fullWidth
                  optionList={
                    serviceOfficeListForUserRolesAutocompleteList || []
                  }
                  disabled={!!disabledServiceOffFromUserRoles}
                  required
                />
              </Grid>
            </Grid>
          </MpgItem>
          <br />

          {!!showCoachRelatedContent && (
            <AdminUserProfileSection
                rhfFormInstance={rhfFormInstance}
                fetchAdminUserProfileLabel={fetchAdminUserProfileLabel}
                adminUserCMSData={adminUserCMSData}
              />
          )}

          {!!showCoachRelatedContent && (
            <Fragment>
              <MpgItem sx={{ padding: { xs: "20px" } }}>
                <Typography
                  variant="h6"
                  align="center"
                  sx={{ fontWeight: "bolder" }}
                >
                  {adminUserCMSData?.pageContent["CronofySection"]?.Title}
                </Typography>

                <Grid container spacing={1}>
                  <Grid item xs={12} sm={12} md={6}>
                    <MpgAutocompleteSingle
                      rhfFormInstance={rhfFormInstance}
                      name={"time_zone_cronofy"}
                      label={fetchCronofySectionLabel(
                        "CronofySection.TimeZone"
                      )}
                      optionList={cronofyTimeZoneAutocompleteList || []}
                      fullWidth
                      disabled={!!disabledTimeZoneCronofy}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={6}>
                    <MpgAutocompleteSingle
                      rhfFormInstance={rhfFormInstance}
                      name={"service_account_name_cronofy"}
                      label={fetchCronofySectionLabel(
                        "CronofySection.ServiceAccountName"
                      )}
                      optionList={serviceAccountDataAutocompleteList || []}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={6}>
                    <MpgAutocompleteMulti
                      rhfFormInstance={rhfFormInstance}
                      name={"available_days"}
                      label={fetchCronofySectionLabel(
                        "CronofySection.AvailableDays"
                      )}
                      fullWidth
                      optionList={weekDaysAutocompleteList || []}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={6}>
                    <MpgAutocompleteSingle
                      rhfFormInstance={rhfFormInstance}
                      name={"working_hours_start_time"}
                      label={fetchCronofySectionLabel(
                        "CronofySection.WorkingHrsStart"
                      )}
                      optionList={hoursListAutocompleteList || []}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={6}>
                    <MpgAutocompleteSingle
                      rhfFormInstance={rhfFormInstance}
                      name={"working_hours_end_time"}
                      label={fetchCronofySectionLabel(
                        "CronofySection.WorkingHrsEnd"
                      )}
                      optionList={hoursListAutocompleteList || []}
                      fullWidth
                      required
                    />
                  </Grid>
                </Grid>
              </MpgItem>
              <br />
            </Fragment>
          )}

          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent={"space-evenly"}
            mt={3}
          >
            <Button variant="outlined" type="submit" size="small">
              {fetchAdminUserDetailsLabel("AdminUserDetails.CreateAdminUser")}
            </Button>
          </Stack>
        </Box>
        {/** error dialog on create action */}
        <MpgErrorDialog
          open={!!isErrorOnCreate}
          title={`${!!isErrorOnCreate && !!errorTitle ? errorTitle : ""}`}
          content={`${!!isErrorOnCreate && !!errorContent ? errorContent : ""}`}
        />

        <MpgLoadingDialog open={!!isLoadingOnCreate} />
      </Box>
    </>
  );
};

export default CreateAdminUser;
